import { cn } from "@/lib/utils";
import {
    useCallback,
    useLayoutEffect,
    useRef,
    type ChangeEvent,
    type ComponentProps,
    type CSSProperties,
} from "react";

const FIELD_SIZING_SUPPORTED =
    typeof CSS !== "undefined" && CSS.supports("field-sizing", "content");

function syncHeight(el: HTMLTextAreaElement) {
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight}px`;
}

type Props = ComponentProps<"textarea">;

/** Textarea that grows with content; disables manual resize handle. */
export function GrowTextarea({ className, onChange, value, style, ...props }: Props) {
    const ref = useRef<HTMLTextAreaElement>(null);

    useLayoutEffect(() => {
        const el = ref.current;
        if (!el || FIELD_SIZING_SUPPORTED) return;
        syncHeight(el);
    }, [value]);

    const handleChange = useCallback(
        (e: ChangeEvent<HTMLTextAreaElement>) => {
            if (!FIELD_SIZING_SUPPORTED) syncHeight(e.target);
            onChange?.(e);
        },
        [onChange],
    );

    const growStyle: CSSProperties | undefined = FIELD_SIZING_SUPPORTED
        ? { fieldSizing: "content", ...style }
        : style;

    return (
        <textarea
            ref={ref}
            className={cn(
                "resize-none overflow-hidden",
                FIELD_SIZING_SUPPORTED && "textarea-grow",
                className,
            )}
            style={growStyle}
            value={value}
            onChange={handleChange}
            {...props}
        />
    );
}
